import {AxiosResponse} from 'axios';
import apiClient from './index';
import {IFiltersData} from '../utils/inTakeAuditReportUtils';

const version = 'v1';
const ecaManagementEndpoint = (path: string) => `${version}/eca${path}`;

export const getDocTypes = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/doc-types?` + params));
};

export const getEcaSummary = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/summary?` + params));
};

export const getProcessingException = (params?: string) => {
  return apiClient.get(
    ecaManagementEndpoint(`/processing-exception?` + params)
  );
};

export const getCustodianEca = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/custodians?` + params));
};

export const getLoadFiles = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/loadfiles?` + params));
};

export const getTagData = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/tags?` + params));
};

export const getDateReport = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/date-report?` + params));
};

export const getTagAuditReports = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/tag/audit/results` + params));
};

export const exportTagReport = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    ecaManagementEndpoint(`/tag/audit/export?` + params),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};

export const downloadCustodiansCsv = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.post<Blob>(
    ecaManagementEndpoint(`/custodians/export/csv?${params}`),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};

export const downloadProcessingExceptionCsv = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.post<Blob>(
    ecaManagementEndpoint(`/processing-exception/export/csv?${params}`),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};

export const downloadDocTypeCsv = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.post<Blob>(
    ecaManagementEndpoint(`/doc-types/export/csv?${params}`),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};
export const downloadFamilyDateCsv = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    ecaManagementEndpoint(`/date-report/export/csv?${params}`),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};

//searches/audit
export const getSearchAuditReport = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/searches/audit?` + params));
};

export const exportSearchReport = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    ecaManagementEndpoint(`/searches/audit/export?` + params),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};

export const getDocumentSearchText = (documentId: string, params: string) => {
  return apiClient.get(
    ecaManagementEndpoint(`/searches/results/${documentId}${params}`)
  );
};

export const getHighlightedDocumentSearchText = (
  documentId: string,
  params: string,
  termIds: string[]
) => {
  const pagination = {
    searchTerm: '',
    sortColumn: '',
    sortOrder: '',
    pageNumber: '1',
    pageSize: '1',
  };
  return apiClient.post(
    ecaManagementEndpoint(
      `/searches/highlightedResults/${documentId}${params}`
    ),
    {termIds: termIds, paginationQuery: pagination}
  );
};

export const getExportList = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/export/list?` + params));
};

export const getExportDetails = (params?: string) => {
  return apiClient.get(ecaManagementEndpoint(`/export/` + params));
};

export const exportOverlayCSV = (
  params?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    ecaManagementEndpoint(`/overlay/export?` + params),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};

export const extractedNativeDownloadTextFile = async (
  filePath: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.post<Blob>(
    ecaManagementEndpoint('/download/extractedTextFile'),
    {filePath},
    {
      responseType: 'blob',
    }
  );
};

export const exportOverlayErrorCSV = (
  params?: string,
  overlayId?: string
): Promise<AxiosResponse<Blob>> => {
  return apiClient.get<Blob>(
    ecaManagementEndpoint(`/overlay/${overlayId}/error?` + params),
    {
      headers: {
        'Content-Type': 'text/csv',
      },
    }
  );
};
export const getOverlay = (params?: string) => {
  console.log('param', params);
  return apiClient.get(ecaManagementEndpoint(`/overlay` + params));
};

export const uploadOverlay = (
  params: string,
  formData: FormData,
  setProgress: (progress: number) => void,
  signal: AbortSignal // Accept AbortSignal for cancellation
): Promise<AxiosResponse> => {
  return apiClient.post(
    `${ecaManagementEndpoint('/overlay/upload')}?${params}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        console.warn('Upload is not computable or event data is missing.');
        if (
          event.lengthComputable &&
          event.loaded !== undefined &&
          event.total !== undefined
        ) {
          const percentComplete = (event.loaded / event.total) * 100;
          setProgress(Math.round(percentComplete));
        } else {
          console.warn(
            'Upload progress is not computable or event data is missing.'
          );
        }
      },
      signal, // Pass the abort signal here to allow the request to be aborted
    }
  );
};

export const createOverlay = (
  params: string,
  body: (IFiltersData | Record<string, unknown>) | string
) => {
  return apiClient.post(
    ecaManagementEndpoint(`/overlay?` + (params ?? '')),
    body
  );
};

export const updateMarkAsLoaded = (params: string, exportId: string) => {
  return apiClient.put(
    ecaManagementEndpoint(`/export/${exportId}/status/loaded` + params)
  );
};

export const getDefaultData = (params?: string, signal?: AbortSignal) => {
  return apiClient.get(
    ecaManagementEndpoint(`/generateOrValidate/defaultdata` + params),
    {signal}
  );
};

export const retryDeleteIndexDocument = (
  params: string,
  indexId: number | string
) => {
  return apiClient.delete(
    ecaManagementEndpoint(
      `/index-maintenance/indexes/${indexId}/documents/retry-delete` + params
    )
  );
};

export const getDownloadDeleteIndexDocumentError = async (
  params: string,
  indexId: number | string
): Promise<AxiosResponse<Blob>> => {
  return await apiClient.get<Blob>(
    ecaManagementEndpoint(
      `/index-maintenance/indexes/${indexId}/faileddocs/download` + params
    ),
    {
      responseType: 'blob',
    }
  );
};